import React, { useContext, Fragment, useState, useEffect } from 'react';
import { Button, NavBar, NoticeBar, PickerView, Popup, Toast } from 'antd-mobile';
import moment from 'moment';
import get from 'lodash/get';
import { ClockCircleOutline, StopOutline, PlayOutline, CloseOutline } from 'antd-mobile-icons';
import styled from 'styled-components';
import { featureEnabled, matchesPermission } from '../utils/permission-helpers';
import { getActiveWorkDescription } from '../utils/helpers';
import WorkEntryPopup from './WorkEntryPopup';
import TotalTimeComponent from './TotalTimeComponent';
import UserSelfContext from '../utils/self-context';
import { startWork } from '../api';

const Description = styled.span`
	font-size: var(--adm-font-size-5);
`;

const Save = styled(Button)`
	font-size: 17px;
`;

const ToastContent = styled.div`
	word-break: normal;
	text-align: center;
	width: 100%;
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const InputContent = styled.div`
	background: #fff;
	margin-top: 12px;
	padding: 12px;
`;

function WorkTimeBar({
	displayIfNotWorking = false
}) {
	const { userSelf, fetchSelf } = useContext(UserSelfContext);
	const [workEntry, setWorkEntry] = useState(userSelf.active_work_entry);
	const [workEntryPopupVisible, setWorkEntryPopupVisible] = useState(false);
	const [startWorkEntryPopupVisible, setStartWorkEntryPopupVisible] = useState(false);
	const [saving, setSaving] = useState(false);
	const [workType, setWorkType] = useState([]);

	useEffect(() => {
		setWorkEntry(userSelf.active_work_entry);
	}, [userSelf]);

	function getActivityButton() {
		if (!workEntry) {
			return <Button
				color='primary'
				size="mini"
				onClick={() => setStartWorkEntryPopupVisible(true)}
			>
				<PlayOutline /> Start
			</Button>;
		}

		return <Button
			color='primary'
			size="mini"
			onClick={() => setWorkEntryPopupVisible(true)}
		>
			<StopOutline /> Stop
		</Button>;
	}

	async function handleWorkEntryChange() {
		setWorkEntryPopupVisible(false);
	}

	async function handleStartWorkEntry() {
		setSaving(true);
		try {
			await startWork({
				parent_id: null,
				parent_type: 'other',
				other_type_value: workType[0]
			});

			setStartWorkEntryPopupVisible(false);
			await fetchSelf(true);

			setSaving(false);

			Toast.show({
				icon: 'success',
				content: 'Work started'
			});
		} catch (err) {
			setSaving(false);
			Toast.show({
				icon: 'fail',
				content: <ToastContent>
					Unable to start work. You might be already working.
				</ToastContent>
			});
		}
	}

	return (
		(
			((workEntry && !displayIfNotWorking) || (displayIfNotWorking && !workEntry)) &&
			featureEnabled(userSelf, 'work_entries_enabled') &&
			matchesPermission(userSelf, 'view_work_entries')
		) ? <Fragment>
				<NoticeBar
					speed={25}
					extra={
						<Fragment>
							<TotalTimeComponent workEntry={workEntry} /> {getActivityButton()}
						</Fragment>
					}
					icon={<ClockCircleOutline />}
					content={<Description>{workEntry ? getActiveWorkDescription(workEntry) : 'Start work...'}</Description>}
					color='info'
				/>
				<WorkEntryPopup
					workEntry={{
						...workEntry,
						work_end: moment().toISOString()
					}}
					visible={workEntryPopupVisible}
					onClose={() => setWorkEntryPopupVisible(false)}
					onSubmit={handleWorkEntryChange}
				/>
				<Popup
					destroyOnClose
					position='right'
					visible={startWorkEntryPopupVisible}
					bodyStyle={{ width: '100%', background: '#f0f2f5' }}
					onClose={() => setStartWorkEntryPopupVisible(false)}
				>
					<Nav
						backArrow={<CloseOutline />}
						onBack={() => setStartWorkEntryPopupVisible(false)}
						back={''}
						right={<Save fill='none' loading={saving} onClick={handleStartWorkEntry}>Start</Save>}
					>
						Select work type
					</Nav>
					<InputContent>
						<PickerView
							columns={[[
								...(get(userSelf, 'settings.work_log_types') || []),
								'Other Work'
							].map(t => ({ label: t, value: t }))]}
							value={workType}
							defaultValue={null}
							onChange={setWorkType}
						/>
					</InputContent>
				</Popup>
			</Fragment> : null
	);
}

export default WorkTimeBar;
